import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import ServiceAgreementPanel from '../components/ServiceAgreementPanel';
import {
  CLIENT_KEY_STORAGE,
  MENTEE_KEY_STORAGE,
  REGISTRATION_PATHWAY,
  REGISTRATION_PATHWAY_STORAGE,
  SCREENS,
  demoCreationTrack,
  isDemoCreationCode,
  normalizeClientKey,
} from '../lib/screenRegistry';
import { isMasterTestingKey } from '../lib/masterTestingKey';
import './RegisterCode.css';
import '../styles/registrationSplit.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');

function useMaintenanceEmbedPreview() {
  useEffect(() => {
    if (window.self === window.top) return undefined;

    document.documentElement.classList.add('maintenance-embed-preview');
    document.body.classList.add('maintenance-embed-preview');
    document.getElementById('root')?.classList.add('maintenance-embed-preview');

    return () => {
      document.documentElement.classList.remove('maintenance-embed-preview');
      document.body.classList.remove('maintenance-embed-preview');
      document.getElementById('root')?.classList.remove('maintenance-embed-preview');
    };
  }, []);
}

function RegisterCodeGate() {
  const navigate = useNavigate();
  const { login, refresh } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const trimmed = code.trim();
    const normalizedCode = normalizeClientKey(trimmed);

    try {
      if (isMasterTestingKey(trimmed)) {
        sessionStorage.setItem(REGISTRATION_PATHWAY_STORAGE, REGISTRATION_PATHWAY.INVITATION);
        await login(trimmed);
        await refresh();
        navigate('/master-admin', { replace: true });
        return;
      }

      if (!isDemoCreationCode(normalizedCode)) {
        setError('Invalid demo key format. Expected PRO-XXX or ELT-XXX (3 letters).');
        return;
      }

      const track = demoCreationTrack(normalizedCode);
      if (track === 'PRO') {
        navigate('/register-mentee-code', { replace: true, state: { demoCode: normalizedCode } });
        return;
      }

      if (track === 'ELT') {
        navigate('/register-code?screen=elt04', { replace: true, state: { demoCode: normalizedCode } });
        return;
      }

      setError('Invalid demo key format. Expected PRO-XXX or ELT-XXX (3 letters).');
    } catch (err: unknown) {
      setError((err as { error?: string }).error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-code-gate-page" data-screen-id={SCREENS.ELT03}>
      <div className="register-code-gate-logo-wrap">
        <img
          src={LOGO}
          alt="Mentology"
          className="register-code-gate-logo brand-logo"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
      <div className="register-code-gate-content">
        <h1 className="section-header register-code-gate-heading">Demo Key Authentication Gate</h1>
        <p className="register-code-gate-intro">
          Enter your provisional validation key to continue to the appropriate demo registration pathway.
        </p>

        <form onSubmit={handleSubmit} className="register-code-gate-form" noValidate>
          <div className="demo-key-entry-field">
            <label htmlFor="elt03-demo-key" className="demo-key-entry-label">
              Enter Demo Key
            </label>
            <p className="demo-key-entry-format">(PRO-XXX / ELT-XXX)</p>
            <input
              id="elt03-demo-key"
              className="input-field"
              placeholder="Enter Demo Key"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoComplete="off"
              aria-label="Enter Demo Key"
            />
          </div>

          {error && <p className="error-text">{error}</p>}

          <div className="button-control-group">
            <button type="submit" className="btn-primary" disabled={loading || !code.trim()}>
              {loading ? 'Verifying...' : 'Continue'}
            </button>
            <Link to="/register-select" className="btn-primary">
              Back
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function RegisterOrgDemoForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefilledCode = (location.state as { demoCode?: string } | null)?.demoCode ?? '';
  const { refresh, login, masterSandboxUnlocked } = useAuth();

  const [code, setCode] = useState(prefilledCode);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (prefilledCode) {
      setCode(prefilledCode);
    }
  }, [prefilledCode]);

  useEffect(() => {
    const lookupCode = normalizeClientKey(code);
    if (!isDemoCreationCode(lookupCode)) return undefined;

    let cancelled = false;
    authApi.lookupDemoCode(lookupCode)
      .then((profile) => {
        if (cancelled) return;
        if (profile.registeredName && !name) setName(profile.registeredName);
        if (profile.registeredEmail && !email) setEmail(profile.registeredEmail);
        if (profile.organisationName && !organisationName) setOrganisationName(profile.organisationName);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [code]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!agreed && !masterSandboxUnlocked && !isMasterTestingKey(code)) {
      setError('The mandatory Service Agreement declaration must be accepted before verification.');
      return;
    }

    if (!name.trim() || !email.trim() || !organisationName.trim() || !businessAddress.trim() || !phone.trim()) {
      setError('Full Name, Organisation Email, Organisation Name, Business Address, and Phone Number are required.');
      return;
    }

    setLoading(true);
    setError('');

    const normalizedCode = normalizeClientKey(code);

    try {
      if (isMasterTestingKey(code.trim())) {
        sessionStorage.setItem(REGISTRATION_PATHWAY_STORAGE, REGISTRATION_PATHWAY.INVITATION);
        await login(code.trim());
        await refresh();
        navigate('/master-admin', { replace: true });
        return;
      }

      sessionStorage.setItem(REGISTRATION_PATHWAY_STORAGE, REGISTRATION_PATHWAY.ORGANISATION);

      if (!isDemoCreationCode(normalizedCode) && !isMasterTestingKey(code.trim())) {
        setError('Invalid demo key format. Expected PRO-XXX or ELT-XXX (3 letters).');
        setLoading(false);
        return;
      }

      const result = await authApi.registerCode({
        code: normalizedCode,
        email,
        name,
        organisationName: organisationName.trim(),
        address: businessAddress.trim(),
        phone: phone.trim(),
        serviceAgreementAccepted: agreed,
      });

      const token = result.orgToken
        || result.demoAccessKey
        || result.userToken
        || normalizedCode;
      const normalizedToken = normalizeClientKey(token);
      sessionStorage.setItem(CLIENT_KEY_STORAGE, normalizedToken);
      sessionStorage.removeItem(MENTEE_KEY_STORAGE);

      await refresh();
      navigate('/introduction', { replace: true });
    } catch (err: unknown) {
      setError((err as { error?: string }).error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="registration-split-screen register-code-screen flex flex-col md:flex-row w-full items-stretch md:items-start"
      data-screen-id={SCREENS.ELT04}
    >
      <aside
        className="registration-split-brand w-full md:w-[30%] flex flex-col items-center bg-transparent border-none p-0 shadow-none overflow-hidden"
        aria-label="Mentology brand"
      >
        <div className="registration-split-logo-anchor bg-transparent border-none p-0 shadow-none">
          <img
            src={LOGO}
            alt="Mentology"
            className="registration-split-logo bg-transparent border-none p-0 shadow-none"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      </aside>

      <main className="registration-split-workspace registration-split-workspace--demo-scroll w-full md:w-[70%] flex flex-col p-6 md:p-8">
        <div className="modal-box register-code-panel">
          <h2 className="section-header">Organisation Demo Mode Setup</h2>

          <form onSubmit={handleSubmit} className="register-code-form" noValidate>
            <div className="demo-key-entry-field">
              <label htmlFor="elt04-demo-key" className="demo-key-entry-label">
                Enter Demo Key
              </label>
              <p className="demo-key-entry-format">(PRO-XXX / ELT-XXX)</p>
              <input
                id="elt04-demo-key"
                className="input-field"
                placeholder="Enter Demo Key"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                aria-label="Enter Demo Key"
              />
            </div>
            <input
              className="input-field"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
            />
            <input
              className="input-field"
              type="email"
              placeholder="Organisation Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <input
              className="input-field"
              placeholder="Organisation Name"
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
              required
              autoComplete="organization"
            />
            <input
              className="input-field"
              placeholder="Business Address"
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              required
              autoComplete="street-address"
            />
            <input
              className="input-field"
              type="tel"
              placeholder="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              autoComplete="tel"
            />

            <ServiceAgreementPanel agreed={agreed} onAgreedChange={setAgreed} variant="compact" />

            {error && <p className="error-text">{error}</p>}

            <div className="button-control-group">
              <button type="submit" className="btn-primary" disabled={loading || (!agreed && !masterSandboxUnlocked && !isMasterTestingKey(code))}>
                {loading ? 'Verifying...' : 'SUBMIT REGISTRATION'}
              </button>
              <Link to="/register-code" className="btn-primary">Back</Link>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

export default function RegisterCode() {
  const [searchParams] = useSearchParams();
  const isElt04Screen = searchParams.get('screen') === 'elt04';

  useMaintenanceEmbedPreview();

  if (isElt04Screen) {
    return <RegisterOrgDemoForm />;
  }

  return <RegisterCodeGate />;
}
